import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge } from '../../components/common/Badge';
import type { BadgeVariant } from '../../components/common/Badge';
import { DataGrid, useDataGridState } from '../../components/common/DataGrid';
import type { ActiveFilter, ColumnDef } from '../../components/common/DataGrid';
import { LogDetailDialog } from '../../components/logs/LogDetailDialog';
import { LogsCopyButton } from '../../components/logs/LogsCopyButton';
import { apiClient } from '../../services/api';
import type { LogLevel, SystemLog, SystemLogsResponse } from '../../types';
import { formatRelativeTime, formatTimestamp } from '../../utils/dateFormat';
import { exportToCsv } from '../../utils/exportTable';
import { truncateText } from '../../utils/formatters';

type RawSystemLogsResponse = Partial<SystemLogsResponse> & {
  logs?: SystemLog[];
};

interface DateRangeFilterValue {
  from?: string;
  to?: string;
}

const LOG_LEVELS: LogLevel[] = ['Trace', 'Debug', 'Information', 'Warning', 'Error', 'Critical'];
const LEVEL_FILTER_ID = 'level';
const DATE_RANGE_FILTER_ID = 'dateRange';
const DEFAULT_HIDDEN_COLUMN_IDS = new Set<string>(['exception', 'userId', 'tenantId', 'requestPath']);
const AUTO_REFRESH_OPTIONS = [5, 10, 30, 60];
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const LEVEL_META: Record<LogLevel, { badgeVariant: BadgeVariant; tone: string; shortLabel: string }> = {
  Trace: {
    badgeVariant: 'neutral',
    tone:
      'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-300 dark:bg-gray-900/60 dark:text-gray-300 dark:ring-gray-700',
    shortLabel: 'Trace',
  },
  Debug: {
    badgeVariant: 'info',
    tone:
      'bg-sky-100 text-sky-700 ring-1 ring-inset ring-sky-300 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800',
    shortLabel: 'Debug',
  },
  Information: {
    badgeVariant: 'success',
    tone:
      'bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800',
    shortLabel: 'Info',
  },
  Warning: {
    badgeVariant: 'warning',
    tone:
      'bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800',
    shortLabel: 'Warning',
  },
  Error: {
    badgeVariant: 'error',
    tone:
      'bg-red-100 text-red-700 ring-1 ring-inset ring-red-300 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-800',
    shortLabel: 'Error',
  },
  Critical: {
    badgeVariant: 'purple',
    tone:
      'bg-purple-100 text-purple-700 ring-1 ring-inset ring-purple-300 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800',
    shortLabel: 'Critical',
  },
};

function LogLevelIcon({ level, className = 'h-3.5 w-3.5' }: { level: LogLevel; className?: string }) {
  if (level === 'Error' || level === 'Critical') {
    return (
      <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M10 1.75a8.25 8.25 0 1 0 0 16.5 8.25 8.25 0 0 0 0-16.5Zm-.75 4a.75.75 0 0 1 1.5 0v5a.75.75 0 0 1-1.5 0v-5Zm.75 8.75a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  if (level === 'Warning') {
    return (
      <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M8.553 3.112a1.75 1.75 0 0 1 2.894 0l6.149 10.143A1.75 1.75 0 0 1 16.15 16H3.85a1.75 1.75 0 0 1-1.446-2.745L8.553 3.112ZM10 7.25a.75.75 0 0 0-.75.75v2.75a.75.75 0 0 0 1.5 0V8A.75.75 0 0 0 10 7.25Zm0 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  if (level === 'Information') {
    return (
      <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M10 1.75a8.25 8.25 0 1 0 0 16.5 8.25 8.25 0 0 0 0-16.5ZM9.25 8a.75.75 0 0 1 1.5 0v5a.75.75 0 0 1-1.5 0V8Zm1.5-2.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M10 1.75A8.25 8.25 0 1 0 10 18.25 8.25 8.25 0 0 0 10 1.75ZM5.75 10a.75.75 0 0 1 .75-.75h6.69l-2.22-2.22a.75.75 0 0 1 1.06-1.06l3.5 3.5a.75.75 0 0 1 0 1.06l-3.5 3.5a.75.75 0 1 1-1.06-1.06l2.22-2.22H6.5A.75.75 0 0 1 5.75 10Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function normalizeSystemLogsResponse(response: RawSystemLogsResponse): SystemLogsResponse {
  const items = Array.isArray(response.items)
    ? response.items
    : Array.isArray(response.logs)
      ? response.logs
      : [];
  const page = typeof response.page === 'number' && response.page > 0 ? response.page : 1;
  const pageSize = typeof response.pageSize === 'number' && response.pageSize > 0 ? response.pageSize : 50;
  const totalCount =
    typeof response.totalCount === 'number' && response.totalCount >= 0
      ? response.totalCount
      : items.length;

  return {
    items,
    totalCount,
    page,
    pageSize,
  };
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function isDateRangeFilterValue(value: unknown): value is DateRangeFilterValue {
  if (!value || typeof value !== 'object') return false;
  const maybeRange = value as Record<string, unknown>;
  return typeof maybeRange.from === 'string' || typeof maybeRange.to === 'string';
}

function getActiveFilterValue(activeFilters: ActiveFilter[], filterId: string): unknown {
  return activeFilters.find((filter) => filter.id === filterId)?.value;
}

function toIsoStartOfDay(date: string): string {
  return `${date}T00:00:00.000Z`;
}

function toIsoEndOfDay(date: string): string {
  return `${date}T23:59:59.999Z`;
}

function getLevelBadgeVariant(level: LogLevel): BadgeVariant {
  return LEVEL_META[level].badgeVariant;
}

function upsertActiveFilter(activeFilters: ActiveFilter[], filterId: string, value: unknown): ActiveFilter[] {
  const nextFilters = activeFilters.filter((filter) => filter.id !== filterId);
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      !Object.values(value as Record<string, unknown>).some(Boolean))
  ) {
    return nextFilters;
  }

  return [...nextFilters, { id: filterId, value }];
}

export function LogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [hiddenColumnIds, setHiddenColumnIds] = useState<Set<string>>(new Set(DEFAULT_HIDDEN_COLUMN_IDS));
  const [activeLog, setActiveLog] = useState<SystemLog | null>(null);
  const correlationIdFilter = searchParams.get('correlationId')?.trim() ?? '';

  const { state, actions } = useDataGridState({
    initialPage: 1,
    initialPageSize: 50,
    initialSort: { field: 'timestamp', direction: 'desc' },
    initialAutoRefreshInterval: 0,
  });

  const selectedLevels = useMemo(() => {
    const rawLevelValue = getActiveFilterValue(state.activeFilters, LEVEL_FILTER_ID);
    if (!isStringArray(rawLevelValue)) return [];
    return rawLevelValue.filter((value): value is LogLevel =>
      LOG_LEVELS.includes(value as LogLevel),
    );
  }, [state.activeFilters]);

  const dateRange = useMemo(() => {
    const rawDateRange = getActiveFilterValue(state.activeFilters, DATE_RANGE_FILTER_ID);
    return isDateRangeFilterValue(rawDateRange) ? rawDateRange : {};
  }, [state.activeFilters]);

  const logsQuery = useQuery<RawSystemLogsResponse, Error, SystemLogsResponse>({
    queryKey: [
      'system-logs',
      state.page,
      state.pageSize,
      state.sort?.field ?? 'timestamp',
      state.sort?.direction ?? 'desc',
      state.debouncedSearch,
      selectedLevels,
      dateRange.from ?? '',
      dateRange.to ?? '',
      correlationIdFilter,
      state.autoRefreshInterval,
    ],
    queryFn: async () => {
      const query = {
        page: state.page,
        pageSize: state.pageSize,
        sortField: state.sort?.field || undefined,
        sortDirection: state.sort?.direction || undefined,
        searchText: state.debouncedSearch || undefined,
        level: selectedLevels.length === 1 ? selectedLevels[0] : undefined,
        levels: selectedLevels.length > 1 ? selectedLevels.join(',') : undefined,
        fromDate: dateRange.from ? toIsoStartOfDay(dateRange.from) : undefined,
        toDate: dateRange.to ? toIsoEndOfDay(dateRange.to) : undefined,
        correlationId: correlationIdFilter || undefined,
      };

      return apiClient.getSystemLogs(query) as Promise<RawSystemLogsResponse>;
    },
    placeholderData: keepPreviousData,
    refetchInterval: state.autoRefreshInterval > 0 ? state.autoRefreshInterval * 1000 : false,
    select: normalizeSystemLogsResponse,
  });

  const logs = useMemo(() => logsQuery.data?.items ?? [], [logsQuery.data]);
  const totalCount = logsQuery.data?.totalCount ?? 0;
  const activeLogIndex = useMemo(() => {
    if (!activeLog) return -1;
    return logs.findIndex((log) => log.id === activeLog.id);
  }, [activeLog, logs]);
  const hasPreviousLog = activeLogIndex > 0;
  const hasNextLog = activeLogIndex >= 0 && activeLogIndex < logs.length - 1;

  const selectedLogs = useMemo(
    () => logs.filter((log) => state.selectedRows.has(log.id)),
    [logs, state.selectedRows],
  );
  const pageSeverityCounts = useMemo(() => {
    return logs.reduce<Record<LogLevel, number>>(
      (accumulator, log) => {
        accumulator[log.level] += 1;
        return accumulator;
      },
      { Trace: 0, Debug: 0, Information: 0, Warning: 0, Error: 0, Critical: 0 },
    );
  }, [logs]);

  const selectedLevelSet = useMemo(() => new Set(selectedLevels), [selectedLevels]);
  const activeFilterCount = useMemo(() => {
    return state.activeFilters.filter((filter) => {
      if (Array.isArray(filter.value)) return filter.value.length > 0;
      if (isDateRangeFilterValue(filter.value)) return Boolean(filter.value.from || filter.value.to);
      return Boolean(filter.value);
    }).length;
  }, [state.activeFilters]);

  const totalVisibleSeverity = useMemo(
    () => selectedLevels.reduce((count, level) => count + pageSeverityCounts[level], 0),
    [pageSeverityCounts, selectedLevels],
  );

  const handlePreviousLog = useCallback(() => {
    if (!hasPreviousLog) return;
    setActiveLog(logs[activeLogIndex - 1] ?? null);
  }, [activeLogIndex, hasPreviousLog, logs]);
  const handleNextLog = useCallback(() => {
    if (!hasNextLog) return;
    setActiveLog(logs[activeLogIndex + 1] ?? null);
  }, [activeLogIndex, hasNextLog, logs]);

  const columns = useMemo<ColumnDef<SystemLog>[]>(
    () => [
      {
        id: 'timestamp',
        header: 'Timestamp',
        accessor: 'timestamp',
        sortable: true,
        minWidth: '210px',
        render: (value) => (
          <div className="leading-tight">
            <div className="inline-flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-100">
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M10 1.75A8.25 8.25 0 1 0 10 18.25 8.25 8.25 0 0 0 10 1.75ZM10.75 6a.75.75 0 0 0-1.5 0v4.19c0 .2.08.39.22.53l2.5 2.5a.75.75 0 1 0 1.06-1.06l-2.28-2.28V6Z"
                  clipRule="evenodd"
                />
              </svg>
              {formatTimestamp(String(value ?? ''))}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {formatRelativeTime(String(value ?? ''))}
            </div>
          </div>
        ),
      },
      {
        id: 'level',
        header: 'Level',
        accessor: 'level',
        sortable: true,
        minWidth: '120px',
        render: (value) => {
          const level = (value as LogLevel | undefined) ?? 'Information';
          const meta = LEVEL_META[level];
          return (
            <Badge variant={getLevelBadgeVariant(level)} size="sm">
              <span className="inline-flex items-center gap-1.5">
                <LogLevelIcon level={level} className="h-3.5 w-3.5" />
                <span className={level === 'Critical' ? 'font-bold' : undefined}>{meta.shortLabel}</span>
              </span>
            </Badge>
          );
        },
      },
      {
        id: 'logger',
        header: 'Logger',
        accessor: 'logger',
        sortable: true,
        minWidth: '180px',
        render: (value) => (
          <span className="inline-flex max-w-full items-center rounded-md bg-gray-100 px-2 py-1 font-mono text-xs text-gray-700 transition-colors group-hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:group-hover:bg-gray-700">
            {String(value ?? '')}
          </span>
        ),
      },
      {
        id: 'message',
        header: 'Message',
        accessor: 'message',
        minWidth: '320px',
        render: (value) => (
          <div className="max-w-xl">
            <p className="truncate text-sm text-gray-900 dark:text-gray-100" title={String(value ?? '')}>
              {truncateText(String(value ?? ''), 140)}
            </p>
          </div>
        ),
      },
      {
        id: 'correlationId',
        header: 'Correlation ID',
        accessor: 'correlationId',
        minWidth: '190px',
        render: (value) => (
          <span className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M7.5 4.75A2.75 2.75 0 0 1 10.25 2h3A2.75 2.75 0 0 1 16 4.75v3a2.75 2.75 0 0 1-2.75 2.75h-3A2.75 2.75 0 0 1 7.5 7.75v-3Zm-3.5 7.5A2.75 2.75 0 0 1 6.75 9.5h3A2.75 2.75 0 0 1 12.5 12.25v3A2.75 2.75 0 0 1 9.75 18h-3A2.75 2.75 0 0 1 4 15.25v-3Z"
                clipRule="evenodd"
              />
            </svg>
            {String(value ?? '—')}
          </span>
        ),
      },
      {
        id: 'userId',
        header: 'User ID',
        accessor: 'userId',
        hidden: hiddenColumnIds.has('userId'),
        minWidth: '170px',
        render: (value) => (
          <span className="font-mono text-xs text-gray-700 dark:text-gray-300">{String(value ?? '—')}</span>
        ),
      },
      {
        id: 'tenantId',
        header: 'Tenant ID',
        accessor: 'tenantId',
        hidden: hiddenColumnIds.has('tenantId'),
        minWidth: '170px',
        render: (value) => (
          <span className="font-mono text-xs text-gray-700 dark:text-gray-300">{String(value ?? '—')}</span>
        ),
      },
      {
        id: 'requestPath',
        header: 'Request Path',
        accessor: 'requestPath',
        hidden: hiddenColumnIds.has('requestPath'),
        minWidth: '220px',
      },
      {
        id: 'exception',
        header: 'Exception',
        accessor: 'exception',
        hidden: hiddenColumnIds.has('exception'),
        minWidth: '280px',
        render: (value) => (
          <span title={String(value ?? '')} className="font-mono text-xs">
            {value ? truncateText(String(value), 160) : '—'}
          </span>
        ),
      },
    ],
    [hiddenColumnIds],
  );

  const visibleColumns = useMemo(
    () => columns.filter((column) => !column.hidden),
    [columns],
  );

  const handleExportCsv = () => {
    exportToCsv(
      logs,
      visibleColumns.map((column) => ({
        header: column.header,
        accessor: (row: SystemLog) => {
          const rawValue =
            typeof column.accessor === 'function' ? column.accessor(row) : row[column.accessor];
          return rawValue === null || rawValue === undefined ? '' : String(rawValue);
        },
      })),
      `system-logs-page-${state.page}`,
    );
  };

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-primary-900 px-6 py-5 text-white dark:from-slate-950 dark:via-slate-900 dark:to-primary-950">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">System Logs</h1>
              <p className="mt-1 text-sm text-white/80">
                Operational stream for incident triage, audit checks, and platform diagnostics.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-xs font-medium ring-1 ring-inset ring-white/20">
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0L7.555 6.678l-4.12.33c-.833.066-1.17 1.102-.536 1.648l3.137 2.686-.958 3.98c-.193.802.69 1.427 1.403.994L10 14.14l3.519 2.176c.713.433 1.596-.192 1.403-.994l-.958-3.98 3.137-2.686c.634-.546.297-1.582-.536-1.648l-4.12-.33-1.577-3.794Z"
                  clipRule="evenodd"
                />
              </svg>
              MSP Operations View
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 md:grid-cols-4 dark:border-gray-700 dark:bg-gray-800/40">
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Rows loaded</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{logs.length}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Total matches</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{totalCount}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Active filters</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{activeFilterCount}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Severity matches (page)
            </p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{totalVisibleSeverity}</p>
          </div>
        </div>
      </div>

      {correlationIdFilter ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-900 shadow-sm dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-200"
          role="status"
          aria-live="polite"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M4.25 5A2.75 2.75 0 0 1 7 2.25h6A2.75 2.75 0 0 1 15.75 5v1.586l1.03 1.03a.75.75 0 0 1-1.06 1.06l-1.03-1.03H7A2.75 2.75 0 0 1 4.25 5Zm2.5 3.75a.75.75 0 0 1 .75.75v2.75A2.75 2.75 0 0 0 10.25 15h2.836l1.444 1.444a.75.75 0 1 0 1.06-1.06L14.146 14h.104A2.75 2.75 0 0 0 17 11.25V9.5a.75.75 0 0 0-1.5 0v1.75c0 .69-.56 1.25-1.25 1.25h-4A1.25 1.25 0 0 1 9 11.25V9.5a.75.75 0 0 1 .75-.75h-3Z"
              clipRule="evenodd"
            />
          </svg>
          <span>
            Filtering by Correlation ID:{' '}
            <span className="font-mono font-medium">{correlationIdFilter}</span>
          </span>
          <button
            type="button"
            onClick={() => {
              setSearchParams((previous) => {
                const next = new URLSearchParams(previous);
                next.delete('correlationId');
                return next;
              });
              actions.setPage(1);
            }}
            className="rounded-md border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-900 transition-colors hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-blue-700 dark:bg-blue-900/40 dark:text-blue-100 dark:hover:bg-blue-900/70"
          >
            Clear Filter
          </button>
        </div>
      ) : null}

      <DataGrid<SystemLog>
        columns={columns}
        data={logs}
        totalCount={totalCount}
        page={state.page}
        pageSize={state.pageSize}
        sort={state.sort}
        loading={logsQuery.isLoading}
        error={logsQuery.error?.message ?? null}
        selectable
        selectedRows={state.selectedRows}
        getRowId={(row) => row.id}
        onSelectionChange={actions.setSelectedRows}
        onPageChange={actions.setPage}
        onPageSizeChange={actions.setPageSize}
        onSortChange={actions.setSort}
        activeFilters={state.activeFilters}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onRowClick={setActiveLog}
        emptyMessage="No system logs found for the selected filters."
        toolbarActions={(
          <div className="w-full space-y-4 border-t border-gray-200 pt-4 dark:border-gray-700">
            <div className="flex w-full flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                Severity
              </span>
              {LOG_LEVELS.map((level) => {
                const isSelected = selectedLevelSet.has(level);
                const meta = LEVEL_META[level];

                return (
                  <button
                    key={level}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => {
                      const nextLevels = isSelected
                        ? selectedLevels.filter((selected) => selected !== level)
                        : [...selectedLevels, level];

                      actions.setFilters(upsertActiveFilter(state.activeFilters, LEVEL_FILTER_ID, nextLevels));
                    }}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors focus:ring-2 focus:ring-primary-500 focus:outline-none ${
                      isSelected
                        ? 'border-primary-700 bg-primary-700 text-white dark:border-primary-500 dark:bg-primary-600'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-gray-500 dark:hover:bg-gray-800'
                    }`}
                  >
                    <LogLevelIcon level={level} />
                    {meta.shortLabel}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => actions.setFilters(upsertActiveFilter(state.activeFilters, LEVEL_FILTER_ID, []))}
                className="rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
              >
                Clear
              </button>
            </div>

            <div className="flex w-full flex-wrap items-end justify-between gap-3">
              <div className="flex min-w-0 flex-1 flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">From</span>
                  <input
                    type="date"
                    value={dateRange.from ?? ''}
                    onChange={(event) =>
                      actions.setFilters(
                        upsertActiveFilter(state.activeFilters, DATE_RANGE_FILTER_ID, {
                          ...dateRange,
                          from: event.target.value,
                        }),
                      )
                    }
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    aria-label="Filter logs from date"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">To</span>
                  <input
                    type="date"
                    value={dateRange.to ?? ''}
                    onChange={(event) =>
                      actions.setFilters(
                        upsertActiveFilter(state.activeFilters, DATE_RANGE_FILTER_ID, {
                          ...dateRange,
                          to: event.target.value,
                        }),
                      )
                    }
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    aria-label="Filter logs to date"
                  />
                </label>
                <label className="flex min-w-56 flex-1 flex-col gap-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Search</span>
                  <input
                    type="search"
                    value={state.search}
                    onChange={(event) => actions.setSearch(event.target.value)}
                    placeholder="Search logs..."
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    aria-label="Search logs"
                  />
                </label>
              </div>

              <div className="flex flex-wrap items-center gap-2 md:justify-end">
                <button
                  type="button"
                  onClick={handleExportCsv}
                  className="inline-flex items-center gap-1 rounded-md bg-primary-700 px-3 py-2 text-sm text-white transition-colors hover:bg-primary-800 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                >
                  <span aria-hidden="true">📄</span>
                  Export CSV
                </button>
                <button
                  type="button"
                  onClick={() => logsQuery.refetch()}
                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  Refresh
                </button>
                <select
                  value={state.autoRefreshInterval}
                  onChange={(event) => actions.setAutoRefreshInterval(Number(event.target.value))}
                  className="rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  aria-label="Auto-refresh interval"
                >
                  <option value={0}>Auto-refresh: Off</option>
                  {AUTO_REFRESH_OPTIONS.map((interval) => (
                    <option key={interval} value={interval}>
                      Auto-refresh: {interval}s
                    </option>
                  ))}
                </select>
                <LogsCopyButton selectedLogs={selectedLogs} onCopied={actions.clearSelection} />
                <details className="relative">
                  <summary className="cursor-pointer list-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700">
                    <span className="inline-flex items-center gap-1.5">
                      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                        <path
                          fillRule="evenodd"
                          d="M2.75 4A1.25 1.25 0 0 1 4 2.75h3.25A1.25 1.25 0 0 1 8.5 4v12a1.25 1.25 0 0 1-1.25 1.25H4A1.25 1.25 0 0 1 2.75 16V4Zm8.75-1.25A1.25 1.25 0 0 0 10.25 4v12a1.25 1.25 0 0 0 1.25 1.25H16A1.25 1.25 0 0 0 17.25 16V4A1.25 1.25 0 0 0 16 2.75h-4.5Z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Columns
                    </span>
                  </summary>
                  <div className="absolute right-0 z-20 mt-2 w-56 rounded-md border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                      Toggle columns
                    </p>
                    <div className="space-y-1">
                      {columns.map((column) => {
                        const checked = !hiddenColumnIds.has(column.id);
                        return (
                          <label
                            key={column.id}
                            className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(event) => {
                                setHiddenColumnIds((current) => {
                                  const next = new Set(current);
                                  if (event.target.checked) next.delete(column.id);
                                  else next.add(column.id);
                                  return next;
                                });
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-primary-700 focus:ring-2 focus:ring-primary-500"
                            />
                            {column.header}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </details>
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-gray-900 dark:text-gray-100">{totalCount}</span> total rows
            </div>
          </div>
        )}
      />

      <LogDetailDialog
        open={Boolean(activeLog)}
        log={activeLog}
        onClose={() => setActiveLog(null)}
        onPrevious={handlePreviousLog}
        onNext={handleNextLog}
        hasPrevious={hasPreviousLog}
        hasNext={hasNextLog}
        position={activeLogIndex >= 0 ? (state.page - 1) * state.pageSize + activeLogIndex + 1 : 0}
        total={totalCount}
      />
    </div>
  );
}
